import React, { memo, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactTooltip from 'react-tooltip';
import { FaInfoCircle } from 'react-icons/fa';
import styles from './ContractsFiltersScreen.module.scss';
import Button from 'common/components/button/Button';
import FormItem from 'common/components/formItem/FormItem';
import Label from 'common/components/label/Label';
import { Col, Row } from 'react-flexbox-grid';
import { SelectValueLabel } from 'common/types/SelectValueLabel';
import Select from 'common/components/select/Select';
import { Controller, useForm } from 'react-hook-form';
import RangeInputPicker from 'common/components/rangeInputPicker/RangeInputPicker';
import ToggleSwitch from 'common/components/toggleSwitch/ToggleSwitch';
import VehiclesService from 'api/vehicles/VehiclesService';
import UseContractsService from 'api/contracts/useContract/UseContractsService';
import MaintenanceContractTypesService from 'api/maintenanceContractTypes/MaintenanceContractTypesService';
import InsuranceContractTypesService from 'api/insuranceContractTypes/InsuranceContractTypesService';
import VehicleBrandsService from 'api/vehicleBrands/VehicleBrandsService';
import VehicleModelsService from 'api/vehicleModels/VehicleModelsService';
import SuppliersService from 'api/suppliers/SuppliersService';
import LocalsService from 'api/locals/LocalsService';
import VehicleSegmentsService from 'api/vehicleSegments/VehicleSegmentsService';
import { CatalogBrandsSearchCriteria } from 'api/catalog/models/CatalogBrandsSearchCriteria';
import { CatalogModelsSearchCriteria } from 'api/catalog/models/CatalogModelsSearchCriteria';
import Utils from 'common/services/Utils';

interface GroupedOption {
    label: string;
    options: SelectValueLabel[];
}

export interface ContractsFilters {
    contractType?: string;
    contractTypeName?: string;
    contractTypeOptionValue?: string;
    state?: string;
    stateName?: string;
    vehicleId?: string;
    vehicleName?: string;
    contractDateFrom?: Date;
    contractDateTo?: Date;
    brandId?: string;
    brandName?: string;
    modelId?: string;
    modelName?: string;
    supplierId?: string;
    supplierName?: string;
    segmentId?: string;
    segmentName?: string;
    localIds?: string[];
    localNames?: string[];
    localOperator?: string;
    hasReminder?: boolean;
    costMin?: number;
    costMax?: number;
    vehiclesInactive?: boolean;
    showOnlyLast?: boolean;
    page?: number;
    orderColumn?: string;
    orderBy?: string;
}

type Props = {
    filters: ContractsFilters;
    onChange?: (filters: ContractsFilters) => void;
    onFilter: (filters: ContractsFilters) => void;
}

const groupedOptionStyles = {
    option: (base: any) => ({ ...base, paddingLeft: '2rem' }),
    groupHeading: (base: any) => ({ ...base, fontWeight: 600, color: '#333', fontSize: '0.85rem', textTransform: 'none' as const }),
};

const sortOptions = (options: SelectValueLabel[]) =>
    [...options].sort((a, b) => (a.label || '').localeCompare(b.label || ''));

const ContractsFiltersScreen: React.FC<Props> = memo(({ filters, onChange, onFilter }: Props) => {
    const { t } = useTranslation();

    const form = useForm<ContractsFilters>({ shouldUnregister: false, shouldFocusError: true, defaultValues: filters });
    const { setValue, getValues, control, watch } = form;

    const [vehiclesOptions, setVehiclesOptions] = useState<SelectValueLabel[]>([]);
    const [contractTypeGroupedOptions, setContractTypeGroupedOptions] = useState<GroupedOption[]>([]);
    const [brandsOptions, setBrandsOptions] = useState<SelectValueLabel[]>([]);
    const [modelsOptions, setModelsOptions] = useState<SelectValueLabel[]>([]);
    const [suppliersOptions, setSuppliersOptions] = useState<SelectValueLabel[]>([]);
    const [segmentsOptions, setSegmentsOptions] = useState<SelectValueLabel[]>([]);
    const [localsOptions, setLocalsOptions] = useState<SelectValueLabel[]>([]);

    const contractTypeOptions: SelectValueLabel[] = [
        { value: 'USE', label: t('contracts_listing.type_use') as string },
        { value: 'MAINTENANCE', label: t('contracts_listing.type_maintenance') as string },
        { value: 'INSURANCE', label: t('contracts_listing.type_insurance') as string },
    ];

    const translateSubTypeName = (name: string): string => {
        if (!name) return name;
        const isSystemType = /^[A-Z_]+$/.test(name);
        if (isSystemType) {
            const keys = [
                `common.use_contract_types.${name}`,
                `common.maintenance_contract_type.${name}`,
                `common.insurance_contract_types.${name}`,
            ];
            for (const key of keys) {
                const translated = t(key as any);
                if (translated !== key) return translated as string;
            }
        }
        return name;
    };

    const reminderOptions: SelectValueLabel[] = [
        { value: 'true', label: t('common.yes') as string },
        { value: 'false', label: t('common.no') as string },
    ];

    const stateOptions: SelectValueLabel[] = [
        { value: 'PLANNED', label: t('contracts.states.planned') as string },
        { value: 'IN_PROGRESS', label: t('contracts.states.in_progress') as string },
        { value: 'EXPIRED', label: t('contracts.states.expired') as string },
    ];

    const flatContractTypeOptions = useMemo(
        () => contractTypeGroupedOptions.flatMap(g => g.options),
        [contractTypeGroupedOptions]
    );

    const loadVehicles = async (withDeleted?: boolean) => {
        try {
            const vehicles = await VehiclesService.catalog({ withDeleted });
            setVehiclesOptions(vehicles);
        } catch {
            // ignore
        }
    };

    const loadCatalogs = async () => {
        try {
            const [useTypes, maintenanceTypes, insuranceTypes, brands, models, suppliers, segments, locals] = await Promise.all([
                UseContractsService.getUseContractTypes(),
                MaintenanceContractTypesService.catalog(),
                InsuranceContractTypesService.catalog(),
                VehicleBrandsService.catalog(),
                VehicleModelsService.catalog(),
                SuppliersService.catalog(),
                VehicleSegmentsService.catalog(),
                LocalsService.catalog(),
            ]);

            const groups: GroupedOption[] = [
                {
                    label: t('contracts_listing.type_use') as string,
                    options: sortOptions(useTypes.map(item => ({
                        value: `USE:${item.value}`,
                        label: translateSubTypeName(item.label),
                    }))),
                },
                {
                    label: t('contracts_listing.type_maintenance') as string,
                    options: sortOptions(maintenanceTypes.map(item => ({
                        value: `MAINTENANCE:${item.value}`,
                        label: translateSubTypeName(item.label),
                    }))),
                },
                {
                    label: t('contracts_listing.type_insurance') as string,
                    options: sortOptions(insuranceTypes.map(item => ({
                        value: `INSURANCE:${item.value}`,
                        label: translateSubTypeName(item.label),
                    }))),
                },
            ];

            setContractTypeGroupedOptions(groups);
            setBrandsOptions(sortOptions(brands));
            setModelsOptions(sortOptions(models));
            setSuppliersOptions(sortOptions(suppliers));
            setSegmentsOptions(Utils.sortAlphabetically(segments));
            setLocalsOptions([{ value: '00000000-0000-0000-0000-000000000000', label: t('maintenances.list.none') as string }, ...Utils.sortAlphabetically(locals)]);
        } catch {
            // ignore
        }
    };

    useEffect(() => {
        loadVehicles(filters.vehiclesInactive);
        loadCatalogs().then(() => {
            if (filters.brandId) {
                setInitialModels();
            }
            if (filters.modelId) {
                setInitialBrands();
            }
        });
    }, []);

    const setInitialModels = async () => {
        const models = await VehicleModelsService.catalog({ brandId: getValues('brandId') } as CatalogModelsSearchCriteria);
        setModelsOptions(sortOptions(models));
    };

    const setInitialBrands = async () => {
        const brands = await VehicleBrandsService.catalog({ modelId: getValues('modelId') } as CatalogBrandsSearchCriteria);
        setBrandsOptions(sortOptions(brands));
    };

    const getModelByBrand = async () => {
        try {
            const models = await VehicleModelsService.catalog({ brandId: getValues('brandId') } as CatalogModelsSearchCriteria);
            setModelsOptions(sortOptions(models));
            if (!getValues('modelId') && models.length === 1) {
                setValue('modelId', models.find(x => x.value)?.value);
                setValue('modelName', models.find(x => x.value)?.label);
                onInputChange();
                getBrandByModel();
            }
        } catch {
            // ignore
        }
    };

    const getBrandByModel = async () => {
        try {
            const brands = await VehicleBrandsService.catalog({ modelId: getValues('modelId') } as CatalogBrandsSearchCriteria);
            setBrandsOptions(sortOptions(brands));
            if (!getValues('brandId') && brands.length === 1) {
                setValue('brandId', brands.find(x => x.value)?.value);
                setValue('brandName', brands.find(x => x.value)?.label);
                onInputChange();
                getModelByBrand();
            }
        } catch {
            // ignore
        }
    };

    const clearFilters = () => {
        form.reset({
            contractType: undefined,
            contractTypeName: undefined,
            contractTypeOptionValue: undefined,
            state: undefined,
            stateName: undefined,
            vehicleId: undefined,
            vehicleName: undefined,
            contractDateFrom: undefined,
            contractDateTo: undefined,
            brandId: undefined,
            brandName: undefined,
            modelId: undefined,
            modelName: undefined,
            supplierId: undefined,
            supplierName: undefined,
            segmentId: undefined,
            segmentName: undefined,
            localIds: [],
            localNames: [],
            localOperator: 'OR',
            hasReminder: undefined,
            costMin: undefined,
            costMax: undefined,
            vehiclesInactive: false,
            showOnlyLast: true,
        });
        loadVehicles(false);
        onInputChange();
        onSubmit();
    };

    const onSubmit = () => {
        onFilter(getValues());
    };

    const onInputChange = () => {
        if (onChange) {
            onChange(form.getValues());
        }
    };

    return (
        <form onSubmit={form.handleSubmit(onSubmit)}>
            <div className={styles.formContent}>
                <FormItem>
                    {/* Ativo | Fornecedor */}
                    <Row>
                        <Col xs={6}>
                            <FormItem>
                                <Label className={styles.bold}>{t('vehicle.vehicle')}</Label>
                                <Controller
                                    render={({ onChange: onSelectChange, value }) => (
                                        <Select
                                            options={vehiclesOptions}
                                            isClearable
                                            placeholder={t('vehicle.vehicle')}
                                            formatOptionLabel={(option: any) => (
                                                <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                                    {option.active === false && (
                                                        <span style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#e04f5f', flexShrink: 0 }} />
                                                    )}
                                                    {option.label}
                                                </span>
                                            )}
                                            onChange={(data: SelectValueLabel) => {
                                                onSelectChange(data?.value ?? null);
                                                setValue('vehicleName', vehiclesOptions.find(x => x.value === data?.value)?.label ?? undefined);
                                                onInputChange();
                                            }}
                                            value={vehiclesOptions.find(x => x.value === value) ?? null}
                                        />
                                    )}
                                    control={control}
                                    name="vehicleId"
                                    defaultValue={getValues('vehicleId')}
                                />
                            </FormItem>
                        </Col>
                        <Col xs={6}>
                            <FormItem>
                                <Label className={styles.bold}>{t('contracts_listing.filters.supplier')}</Label>
                                <Controller
                                    render={({ onChange: onSelectChange, value }) => (
                                        <Select
                                            options={suppliersOptions}
                                            isClearable
                                            placeholder={t('contracts_listing.filters.supplier')}
                                            onChange={(data: SelectValueLabel) => {
                                                onSelectChange(data?.value ?? null);
                                                setValue('supplierName', data?.label ?? undefined);
                                                onInputChange();
                                            }}
                                            value={suppliersOptions.find(x => x.value === value) ? { label: suppliersOptions.find(x => x.value === value)?.label ?? '', value } : null}
                                        />
                                    )}
                                    control={control}
                                    name="supplierId"
                                    defaultValue={getValues('supplierId')}
                                />
                            </FormItem>
                        </Col>
                    </Row>

                    {/* Marca | Modelo */}
                    <Row>
                        <Col xs={6}>
                            <FormItem>
                                <Label className={styles.bold}>{t('contracts_listing.filters.brand')}</Label>
                                <Controller
                                    render={({ onChange: onSelectChange, value }) => (
                                        <Select
                                            options={brandsOptions}
                                            isClearable
                                            placeholder={t('contracts_listing.filters.brand')}
                                            onChange={(data: SelectValueLabel) => {
                                                onSelectChange(data?.value ?? null);
                                                setValue('brandName', data?.label ?? undefined);
                                                getModelByBrand();
                                                onInputChange();
                                            }}
                                            value={brandsOptions.find(x => x.value === value) ? { label: brandsOptions.find(x => x.value === value)?.label ?? '', value } : null}
                                        />
                                    )}
                                    control={control}
                                    name="brandId"
                                    defaultValue={getValues('brandId')}
                                />
                            </FormItem>
                        </Col>
                        <Col xs={6}>
                            <FormItem>
                                <Label className={styles.bold}>{t('contracts_listing.filters.model')}</Label>
                                <Controller
                                    render={({ onChange: onSelectChange, value }) => (
                                        <Select
                                            options={modelsOptions}
                                            isClearable
                                            placeholder={t('contracts_listing.filters.model')}
                                            onChange={(data: SelectValueLabel) => {
                                                onSelectChange(data?.value ?? null);
                                                setValue('modelName', data?.label ?? undefined);
                                                getBrandByModel();
                                                onInputChange();
                                            }}
                                            value={modelsOptions.find(x => x.value === value) ? { label: modelsOptions.find(x => x.value === value)?.label ?? '', value } : null}
                                        />
                                    )}
                                    control={control}
                                    name="modelId"
                                    defaultValue={getValues('modelId')}
                                />
                            </FormItem>
                        </Col>
                    </Row>

                    {/* Segmento | Agência */}
                    <Row>
                        <Col xs={6}>
                            <FormItem>
                                <Label className={styles.bold}>{t('vehicle.type')}</Label>
                                <Select
                                    options={segmentsOptions}
                                    isClearable
                                    placeholder={t('vehicle.type')}
                                    onChange={(data: SelectValueLabel) => {
                                        setValue('segmentId', data?.value ?? undefined);
                                        setValue('segmentName', data?.label ?? undefined);
                                        onInputChange();
                                    }}
                                    value={segmentsOptions.find(x => x.value === getValues('segmentId')) || null}
                                />
                            </FormItem>
                        </Col>
                        <Col xs={6}>
                            <FormItem>
                                <div className={styles.labelWithOperator}>
                                    <Label className={styles.bold}>{t('vehicle.local')}</Label>
                                    <div className={styles.operatorToggle}>
                                        <span title={t('common.filter_all_tooltip' as any) as string}>
                                            <Button
                                                text={t('common.filter_all' as any) as string}
                                                className={styles.firstChild}
                                                preset={watch('localOperator') === 'AND' ? 'primary' : 'secondary'}
                                                onClick={() => { setValue('localOperator', 'AND'); onInputChange(); }}
                                                size={'small'}
                                            />
                                        </span>
                                        <span title={t('common.filter_any_tooltip' as any) as string}>
                                            <Button
                                                text={t('common.filter_any' as any) as string}
                                                className={styles.lastChild}
                                                preset={watch('localOperator') !== 'AND' ? 'primary' : 'secondary'}
                                                onClick={() => { setValue('localOperator', 'OR'); onInputChange(); }}
                                                size={'small'}
                                            />
                                        </span>
                                    </div>
                                </div>
                                <Controller
                                    render={({ onChange: onSelectChange, value }) => (
                                        <Select
                                            options={localsOptions}
                                            isMulti
                                            isClearable
                                            placeholder={t('vehicle.local')}
                                            onChange={(data: SelectValueLabel[]) => {
                                                onSelectChange(data?.map(d => d.value) ?? []);
                                                setValue('localNames', data?.map(d => d.label) ?? []);
                                                onInputChange();
                                            }}
                                            value={localsOptions.filter(x => ((value as string[]) ?? []).includes(x.value))}
                                        />
                                    )}
                                    control={control}
                                    name="localIds"
                                    defaultValue={getValues('localIds') || []}
                                />
                            </FormItem>
                        </Col>
                    </Row>

                    {/* Tipo de contrato | Tipo */}
                    <Row>
                        <Col xs={6}>
                            <FormItem>
                                <Label className={styles.bold}>{t('contracts_listing.filters.contract_type')}</Label>
                                <Controller
                                    render={({ onChange: onSelectChange, value }) => (
                                        <Select
                                            options={contractTypeOptions}
                                            isClearable
                                            placeholder={t('contracts_listing.filters.contract_type')}
                                            onChange={(data: SelectValueLabel) => {
                                                onSelectChange(data?.value ?? null);
                                                setValue('contractTypeName', data?.label ?? undefined);
                                                setValue('contractTypeOptionValue', undefined);
                                                onInputChange();
                                            }}
                                            value={contractTypeOptions.find(x => x.value === value) ?? null}
                                        />
                                    )}
                                    control={control}
                                    name="contractType"
                                    defaultValue={getValues('contractType')}
                                />
                            </FormItem>
                        </Col>
                        <Col xs={6}>
                            <FormItem>
                                <Label className={styles.bold}>{t('contracts_listing.filters.sub_type')}</Label>
                                <Select
                                    options={contractTypeGroupedOptions as any}
                                    isClearable
                                    placeholder={t('contracts_listing.filters.sub_type')}
                                    styles={groupedOptionStyles}
                                    onChange={(data: SelectValueLabel) => {
                                        if (data?.value) {
                                            const mainType = data.value.split(':')[0];
                                            setValue('contractType', mainType);
                                            setValue('contractTypeName', contractTypeOptions.find(x => x.value === mainType)?.label ?? undefined);
                                        }
                                        setValue('contractTypeOptionValue', data?.value ?? undefined);
                                        onInputChange();
                                    }}
                                    value={flatContractTypeOptions.find(x => x.value === getValues('contractTypeOptionValue')) ?? null}
                                />
                            </FormItem>
                        </Col>
                    </Row>

                    {/* Estado | Lembrete */}
                    <Row>
                        <Col xs={6}>
                            <FormItem>
                                <Label className={styles.bold}>{t('contracts_listing.filters.state')}</Label>
                                <Controller
                                    render={({ onChange: onSelectChange, value }) => (
                                        <Select
                                            options={stateOptions}
                                            isClearable
                                            placeholder={t('contracts_listing.filters.state')}
                                            onChange={(data: SelectValueLabel) => {
                                                onSelectChange(data?.value ?? null);
                                                setValue('stateName', data?.label ?? undefined);
                                                onInputChange();
                                            }}
                                            value={stateOptions.find(x => x.value === value) ?? null}
                                        />
                                    )}
                                    control={control}
                                    name="state"
                                    defaultValue={getValues('state')}
                                />
                            </FormItem>
                        </Col>
                        <Col xs={6}>
                            <FormItem>
                                <Label className={styles.bold}>{t('contracts_listing.filters.has_reminder')}</Label>
                                <Select
                                    options={reminderOptions}
                                    isClearable
                                    placeholder={t('contracts_listing.filters.has_reminder')}
                                    onChange={(data: SelectValueLabel) => {
                                        setValue('hasReminder', data?.value === 'true' ? true : data?.value === 'false' ? false : undefined);
                                        onInputChange();
                                    }}
                                    value={getValues('hasReminder') === true
                                        ? reminderOptions.find(x => x.value === 'true') ?? null
                                        : getValues('hasReminder') === false
                                            ? reminderOptions.find(x => x.value === 'false') ?? null
                                            : null}
                                />
                            </FormItem>
                        </Col>
                    </Row>

                    {/* Data dos contratos */}
                    <Row>
                        <Col xs={12}>
                            <Controller
                                render={({ onChange: onStartChange, value: startValue }) => (
                                    <Controller
                                        render={({ onChange: onEndChange, value: endValue }) => (
                                            <RangeInputPicker
                                                isDatePicker
                                                label={t('contracts_listing.filters.contract_date_range' as any) as string}
                                                tooltip={t('contracts_listing.filters.contract_date_range_tooltip' as any) as string}
                                                defaultStartValue={startValue ? new Date(startValue) : undefined}
                                                defaultEndValue={endValue ? new Date(endValue) : undefined}
                                                onChange={(start, end) => {
                                                    onStartChange(start);
                                                    onEndChange(end);
                                                    onInputChange();
                                                }}
                                            />
                                        )}
                                        control={control}
                                        name="contractDateTo"
                                        defaultValue={getValues('contractDateTo')}
                                    />
                                )}
                                control={control}
                                name="contractDateFrom"
                                defaultValue={getValues('contractDateFrom')}
                            />
                        </Col>
                    </Row>

                    {/* Custo */}
                    <Row>
                        <Col xs={12}>
                            <RangeInputPicker
                                key="costRange"
                                label={t('contracts_listing.filters.cost')}
                                onChange={(startValue: any, endValue: any) => {
                                    setValue('costMin', startValue || startValue === 0 ? Number(startValue) : undefined);
                                    setValue('costMax', endValue || endValue === 0 ? Number(endValue) : undefined);
                                    onInputChange();
                                }}
                                defaultStartValue={getValues('costMin')}
                                defaultEndValue={getValues('costMax')}
                                groupSymbol={t('common.euro')}
                            />
                        </Col>
                    </Row>

                    {/* Incluir ativos inativos | Mostrar o último */}
                    <Row>
                        <Col xs={6}>
                            <FormItem>
                                <Label className={styles.bold}>{t('contracts_listing.filters.include_inactive')}</Label>
                                <ToggleSwitch
                                    name="vehiclesInactive"
                                    form={form}
                                    changeToggle={() => {
                                        loadVehicles(form.getValues('vehiclesInactive'));
                                        onInputChange();
                                    }}
                                />
                            </FormItem>
                        </Col>
                        <Col xs={6}>
                            <FormItem>
                                <Label className={styles.bold}>
                                    {t('contracts_listing.filters.show_only_last')}
                                    <FaInfoCircle data-tip={t('contracts_listing.filters.show_only_last_tooltip')} data-for="showOnlyLastTooltip" style={{ marginLeft: '0.5rem' }} />
                                </Label>
                                <ReactTooltip id="showOnlyLastTooltip" place="left" type="dark" effect="solid" />
                                <ToggleSwitch
                                    name="showOnlyLast"
                                    form={form}
                                    changeToggle={onInputChange}
                                />
                            </FormItem>
                        </Col>
                    </Row>
                </FormItem>

                <div className={styles.buttonsContainer}>
                    <Button type="button" preset="secondary" onClick={clearFilters}>
                        {t('common.clear')}
                    </Button>
                    <Button type="submit" preset="primary">
                        {t('common.apply')}
                    </Button>
                </div>
            </div>
        </form>
    );
});

ContractsFiltersScreen.displayName = 'ContractsFiltersScreen';

export default ContractsFiltersScreen;
